import React, { createContext, useContext, useState, useEffect } from 'react';
import { PublicTenantInfo } from '@saas/shared-types';
import { api } from '../services/api';

interface TenantContextValue {
  subdomain: string;
  tenantInfo: PublicTenantInfo | null;
  isLoading: boolean;
  error: string | null;
  switchTenant: (newSubdomain: string) => Promise<void>;
  refreshTenantInfo: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subdomain, setSubdomain] = useState<string>(() => {
    // Check if running on a real subdomain (e.g. acme.domain.com)
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    if (parts.length >= 3 && parts[0] !== 'www' && parts[0] !== 'localhost') {
      return parts[0];
    }
    return api.getTenantSubdomain() || 'acme';
  });

  const [tenantInfo, setTenantInfo] = useState<PublicTenantInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTenant = async (targetSubdomain: string) => {
    setIsLoading(true);
    setError(null);
    try {
      api.setTenantSubdomain(targetSubdomain);
      const data = await api.getPublicTenantInfo(targetSubdomain);
      setTenantInfo(data);
      setSubdomain(targetSubdomain);
    } catch (err: any) {
      console.warn(`[TenantContext] Workspace "${targetSubdomain}" not found:`, err.message);
      setError(err.message || 'Unable to load workspace details.');
      setTenantInfo(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTenant(subdomain);
  }, [subdomain]);

  const switchTenant = async (newSubdomain: string) => {
    await fetchTenant(newSubdomain);
  };

  const refreshTenantInfo = async () => {
    await fetchTenant(subdomain);
  };

  return (
    <TenantContext.Provider
      value={{
        subdomain,
        tenantInfo,
        isLoading,
        error,
        switchTenant,
        refreshTenantInfo,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextValue => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
